import { type PropsWithChildren, useEffect, useRef } from "react";

import { CloseButton } from "@vega-core/components/Buttons/CloseButton/CloseButton";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

type Props = {
  hideCloseButton: boolean;
  isClosing: boolean;
  isOpen: boolean;
  onClose: VoidFunction;
  noPadding: boolean;
  noScrolling: boolean;
  customWidthClassnames?: string;
  dataTestId: string;
};

export function SidebarModalPanel({
  children,
  hideCloseButton,
  isClosing,
  isOpen,
  onClose,
  noPadding,
  noScrolling,
  customWidthClassnames,
  dataTestId,
}: PropsWithChildren<Props>) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isOpen && isTruthy(panelRef.current?.scrollTop)) {
      // if panel is scrolled from previous usage
      panelRef.current.scrollTo(0, 0);
    }
  }, [isOpen]);

  return (
    <div
      className={cnTwMerge(
        "sidebar-modal-panel bg-background/primary border-2 border-border/secondary w-[100vw] flex flex-col",
        {
          hidden: !isOpen,
          "sidebar-closed": isClosing,
          "sidebar-open": isOpen && !isClosing,
          "sm:w-sidebar-modal sm:right-2": customWidthClassnames === undefined,
        },
        customWidthClassnames,
      )}
      data-testid={`${dataTestId}-side-panel`}
      onClick={(e) => e.stopPropagation()}
      style={{ height: "100vh" }}
    >
      {/* Sticky Header Section */}
      <div className="flex-shrink-0">
        {!hideCloseButton ? (
          <div
            className={cnTwMerge(
              "float-right w-8 cursor-pointer text-text/primary",
              {
                "mt-4 mr-4 sm:mt-6 sm:mr-6": noPadding,
                "mt-6 mr-6": !noPadding,
              },
            )}
          >
            <CloseButton onClick={onClose} />
          </div>
        ) : null}
      </div>

      {/* Scrollable Content Section */}
      <div
        className={cnTwMerge("flex-1 min-h-0", {
          "overflow-scroll overflow-x-hidden": !noScrolling,
        })}
        data-testid={`${dataTestId}-scroll-region`}
        ref={panelRef}
      >
        <div
          className={cnTwMerge({
            "p-6": !noPadding,
            "p-0": noPadding,
          })}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
